import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowsRotate,
  faCircleCheck,
  faCircleExclamation,
  faExternalLinkAlt,
  faGear,
  faPlugCircleBolt,
  faRobot,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useMessage } from "../../../context/useMessageContext"
import { useRespostaAiIntegration } from "../../../hooks/RespostaAi/useRespostaAiIntegration"
import type { IRespostaAiSettings } from "../../../Objects/RespostaAi/IRespostaAiSettings"
import { RespostaAiSetupPopup } from "./RespostaAiSetupPopup"

const RESPOSTA_AI_TRANSPORT = 1

const DESKTOP_AGENT_TRANSPORT = 0

const META_OFFICIAL_TRANSPORT = 2

export const RespostaAiIntegrationPage: React.FC = () => {
  const { t } = useTranslation(["pages", "helpPopup"])

  const { setMessage } = useMessage()

  const { getSettings, syncKnowledge } = useRespostaAiIntegration()

  const [settings, setSettings] = useState<IRespostaAiSettings | null>(null)

  const [isSetupOpen, setIsSetupOpen] = useState(false)

  const activeTransportLabel = useMemo(() => {
    switch (settings?.activeTransport) {
      case RESPOSTA_AI_TRANSPORT:
        return t("label_resposta_ai_transport_resposta_ai")
      case META_OFFICIAL_TRANSPORT:
        return t("label_resposta_ai_transport_meta")
      default:
        return t("label_resposta_ai_transport_windows")
    }
  }, [settings?.activeTransport, t])

  const isRespostaAiActive = settings?.activeTransport === RESPOSTA_AI_TRANSPORT

  const isConfigured = settings?.hasGlobalApiKey && settings.hasOperationalApiKey

  const loadSettings = async () => {
    const current = await getSettings()

    setSettings(current)
  }

  useEffect(() => {
    void loadSettings()
  }, [])

  const handleSyncKnowledge = async () => {
    const current = await syncKnowledge()

    setSettings(current)
    setMessage(t("label_resposta_ai_sync_success_title"), t("label_resposta_ai_sync_success_description"))
  }

  return (
    <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-2 sm:p-4">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="min-w-0">
          <div className="mb-3 mt-3 flex flex-row items-start justify-between p-2">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                <FontAwesomeIcon icon={faRobot} className="text-sm" />
              </span>

              {t("label_resposta_ai_title")}
            </h2>

            <PopupHelpButton
              variant="global"
              helpKey="pages.resposta-ai-integration"
              title={t("label_resposta_ai_title")}
              description={t("label_resposta_ai_subtitle")}
              bullets={t("helpPopup:global.respostaAiIntegration.bullets", { returnObjects: true }) as string[]}
              steps={t("helpPopup:global.respostaAiIntegration.steps", { returnObjects: true }) as string[]}
            />
          </div>

          <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
            <section className="rounded-lg border-2 border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faShieldHalved} className="text-sky-700" />
                    <strong className="text-sm text-slate-900">{t("label_resposta_ai_active_transport_title")}</strong>
                  </div>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {t("label_resposta_ai_active_transport_description", { name: activeTransportLabel })}
                  </p>
                </div>

                <span
                  className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full border px-3 py-1 text-xs font-black ${
                    isRespostaAiActive
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-slate-50 text-slate-700"
                  }`}
                >
                  <FontAwesomeIcon icon={isRespostaAiActive ? faCircleCheck : faCircleExclamation} />
                  {isRespostaAiActive ? t("label_resposta_ai_active") : t("label_resposta_ai_inactive")}
                </span>
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                <span
                  className={`inline-flex min-h-11 items-center justify-center rounded-lg border-2 px-3 py-2 text-sm font-bold ${
                    settings?.activeTransport === DESKTOP_AGENT_TRANSPORT
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  {t("label_resposta_ai_transport_windows")}
                </span>

                <span
                  className={`inline-flex min-h-11 items-center justify-center rounded-lg border-2 px-3 py-2 text-sm font-bold ${
                    settings?.activeTransport === META_OFFICIAL_TRANSPORT
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  {t("label_resposta_ai_transport_meta")}
                </span>

                <span
                  className={`inline-flex min-h-11 items-center justify-center rounded-lg border-2 px-3 py-2 text-sm font-bold ${
                    settings?.activeTransport === RESPOSTA_AI_TRANSPORT
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  {t("label_resposta_ai_transport_resposta_ai")}
                </span>
              </div>
            </section>

            <section className="rounded-lg border-2 border-slate-200 bg-slate-50 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faPlugCircleBolt} className="text-emerald-700" />
                <strong className="text-sm text-slate-900">{t("label_resposta_ai_status_title")}</strong>
              </div>

              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <p>
                  <strong>{t("label_resposta_ai_status")}:</strong> {settings?.status ?? "-"}
                </p>
                <p>
                  <strong>{t("label_resposta_ai_instance")}:</strong> {settings?.instanceId ?? "-"}
                </p>
                <p>
                  <strong>{t("label_resposta_ai_last_sync")}:</strong>{" "}
                  {settings?.lastKnowledgeSyncAt ? new Date(settings.lastKnowledgeSyncAt).toLocaleString() : "-"}
                </p>
              </div>

              {settings?.lastError && (
                <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs font-semibold text-rose-800">
                  {settings.lastError}
                </p>
              )}
            </section>
          </div>

          <section className="mt-3 rounded-lg border-2 border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <strong className="text-sm text-slate-900">{t("label_resposta_ai_actions_title")}</strong>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{t("label_resposta_ai_actions_description")}</p>
              </div>

              <div className="grid gap-2 sm:flex sm:flex-wrap sm:justify-end">
                <button
                  type="button"
                  onClick={() => setIsSetupOpen(true)}
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiIntegrationPage.button.openSetup"
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-sky-700"
                >
                  <FontAwesomeIcon icon={faGear} />
                  {isConfigured ? t("label_resposta_ai_manage_setup") : t("label_resposta_ai_start_setup")}
                </button>

                <button
                  type="button"
                  onClick={() => void loadSettings()}
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiIntegrationPage.button.refresh"
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <FontAwesomeIcon icon={faArrowsRotate} />
                  {t("label_resposta_ai_refresh")}
                </button>

                <button
                  type="button"
                  onClick={() => void handleSyncKnowledge()}
                  disabled={!settings?.hasOperationalApiKey}
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiIntegrationPage.button.syncKnowledge"
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border-2 border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 shadow-sm hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faPlugCircleBolt} />
                  {t("label_resposta_ai_sync_knowledge")}
                </button>

                {settings?.panelUrl && (
                  <a
                    href={settings.panelUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiIntegrationPage.link.openPanel"
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border-2 border-sky-200 bg-white px-4 py-2 text-sm font-bold text-sky-700 shadow-sm hover:bg-sky-50"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    {t("label_resposta_ai_open_panel")}
                  </a>
                )}
              </div>
            </div>
          </section>
        </div>
      </Animate>

      <RespostaAiSetupPopup
        isOpen={isSetupOpen}
        settings={settings}
        onSettingsChange={setSettings}
        onClose={() => setIsSetupOpen(false)}
      />
    </div>
  )
}
